import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Heart, Phone, Instagram, Youtube, Facebook } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/book", label: "Book Consult" },
  { to: "/ebooks", label: "E-Books" },
  { to: "/content", label: "Content" },
  { to: "/tips", label: "Parenting Tips" },
  { to: "/trackers", label: "Trackers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { lang, setLang } = useLang();
  const nav = useNavigate();
  const loc = useLocation();

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-root">
      <header className="glass-header sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{background: "linear-gradient(135deg,#2D7A78,#4FA8A6)"}}>
              <Heart className="text-white" strokeWidth={2.2} size={20} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[15px] sm:text-[17px]" style={{color:"#232F2F"}}>Dr. Hariom</div>
              <div className="text-[10px] tracking-widest uppercase" style={{color:"#5A6D6D"}}>Pediatric Care</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  loc.pathname === n.to ? "bg-[#CBE4E8] text-[#24615F]" : "text-[#232F2F] hover:bg-[#F0F3F3]"
                }`}
              >{n.label}</Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="hidden sm:inline-flex text-xs font-semibold border rounded-full px-3 py-1.5"
              style={{borderColor:"#E5EBEB", color:"#5A6D6D"}}
              data-testid="lang-toggle"
            >{lang === "en" ? "EN | हिंदी" : "हिंदी | EN"}</button>

            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="btn-outline text-sm hidden sm:inline-flex" data-testid="dashboard-link">
                  Dashboard
                </Link>
                <button onClick={() => { logout(); nav("/"); }} className="text-sm text-[#B3261E] font-semibold px-2" data-testid="logout-btn">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-outline text-sm" data-testid="login-btn">Login</Link>
              </>
            )}

            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X size={22}/> : <Menu size={22}/>}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#E5EBEB] px-4 py-3 bg-white" data-testid="mobile-nav">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2 text-sm font-medium text-[#232F2F]">{n.label}</Link>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-16 border-t border-[#E5EBEB] bg-white" data-testid="app-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-4 gap-8">
          <div>
            <div className="font-display font-extrabold text-lg" style={{color:"#232F2F"}}>Dr. Hariom Choudhary</div>
            <div className="text-sm text-[#5A6D6D] mt-1">Pediatrician & Neonatologist</div>
            <div className="text-xs text-[#5A6D6D] mt-1">Reg. No.: RMC34455</div>
            <div className="mt-4 flex gap-3">
              <a href="https://www.instagram.com/dr_hariom_pediatrician/" target="_blank" rel="noreferrer" className="text-[#5A6D6D] hover:text-[#2D7A78]" data-testid="footer-ig"><Instagram size={18}/></a>
              <a href="https://youtube.com/@drhariompediatrician" target="_blank" rel="noreferrer" className="text-[#5A6D6D] hover:text-[#2D7A78]" data-testid="footer-yt"><Youtube size={18}/></a>
              <a href="https://wa.me/916350018891" target="_blank" rel="noreferrer" className="text-[#5A6D6D] hover:text-[#2D7A78]" data-testid="footer-wa"><Phone size={18}/></a>
              <a href="#" className="text-[#5A6D6D] hover:text-[#2D7A78]"><Facebook size={18}/></a>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Care</div>
            <ul className="space-y-2 text-sm text-[#5A6D6D]">
              <li><Link to="/book">Book Consultation</Link></li>
              <li><Link to="/trackers">Growth Trackers</Link></li>
              <li><Link to="/dashboard">My Dashboard</Link></li>
              <li><Link to="/tips">Parenting Tips</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">Learn</div>
            <ul className="space-y-2 text-sm text-[#5A6D6D]">
              <li><Link to="/ebooks">E-Books</Link></li>
              <li><Link to="/content">Reels & Videos</Link></li>
              <li><Link to="/about">About Dr. Hariom</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">Contact</div>
            <div className="text-sm text-[#5A6D6D]">+91 63500 18891</div>
            <div className="text-sm text-[#5A6D6D]">drhariom09999@gmail.com</div>
            <div className="text-xs text-[#5A6D6D] mt-4 leading-relaxed">
              Content on this platform is for parent education. It does not replace physical consultation when needed. In emergency, visit the nearest hospital.
            </div>
          </div>
        </div>
        <div className="border-t border-[#E5EBEB] py-4 text-center text-xs text-[#5A6D6D]">
          © {new Date().getFullYear()} Dr. Hariom Pediatric Care — Make Parenting Joyful
        </div>
      </footer>
    </div>
  );
}
